import { useState, useEffect } from "react";
import ChangePassword from "../../../cors/ChangePassword";
import { UserCheck } from 'lucide-react';
import { BASE_URL } from '../../../apiConfig';
import Cookies from 'js-cookie';
import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { toast } from "sonner"

export default function ProfileConfirmator({ user }) {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    rib: "",
    gender: "Male",
  });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (user && user.confirmator) {
      setFormData({
        email: user.email || "",
        name: user.confirmator.name || "",
        phone: user.confirmator.phone || "",
        rib: user.confirmator.rib || "",
        gender: user.confirmator.gender || "Male",
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    // Validation du téléphone
    if (!formData.phone || typeof formData.phone !== 'string') {
      errors.phone = [t("profile.confirmator.validation_phone")];
    }
    
    // Validation de l'email
    const emailRegex = /^[^\s.]+.[^\s.]+\.[^\s.]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = [t("profile.confirmator.validation_email")];
    }
    
    // Validation du nom
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = [t("profile.confirmator.validation_name")];
    }
    
    return errors;
  };

  const handleSubmit = async () => {
     const clientErrors = validateForm();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      
      let errorMessages = [];
      Object.keys(clientErrors).forEach(field => {
        errorMessages.push(...clientErrors[field]);
      });
      
      toast.error(t("profile.confirmator.validation_error"));
      return;
    }
    setIsLoading(true);
    setFieldErrors({});
    
    try {
      const response = await fetch(`${BASE_URL}/api/update-confirmator-profile/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        Cookies.set("user", JSON.stringify(result.user), { expires: 7 });
        setIsEditing(false);
        toast.success(t("profile.confirmator.update_success"));
      } else {
        if (result.errors) {
          setFieldErrors(result.errors);
          
          let errorMessages = [];
          Object.keys(result.errors).forEach(field => {
            result.errors[field].forEach(error => {
              errorMessages.push(error);
            });
          });
          
          
        }
        toast.error(t("profile.confirmator.update_error"));
      }
    } catch (error) {
      toast.error(t("profile.confirmator.update_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (user && user.confirmator) {
      setFormData({
        email: user.email || "",
        name: user.confirmator.name || "",
        phone: user.confirmator.phone || "",
        rib: user.confirmator.rib || "",
        gender: user.confirmator.gender || "Male",
      });
    }
    setIsEditing(false);
    setFieldErrors({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("profile.confirmator.not_available");
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (!user || !user.confirmator) {
    return <div className="max-w-6xl mx-auto p-6">{t('profile.confirmator.loading')}</div>;
  }

  const confirmatorData = user.confirmator;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 md:w-32 md:h-32 bg-white/20 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                <UserCheck className="h-10 w-10 md:w-16 md:h-16 text-white" />
              </div>
              <div className="text-white">
                <h1 className="text-xl md:text-2xl font-bold">{confirmatorData.name}</h1>
                <p className="text-xs text-emerald-100 mb-1">{user.email}</p>
                <p className="text-emerald-100 text-xs">{t('profile.confirmator.subtitle')}</p>
              </div>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-xl font-bold ">{t('profile.confirmator.badge')}</div>
                <div className="text-xs text-emerald-500">{t('profile.confirmator.trust_score')}</div>
              </div>
            </div>
          </div>
        </div>      
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="p-8 max-xl:px-5">
          <div className="space-y-6"> 
            <div>
              {!isEditing ? (
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span className="text-sm max-xl:text-xs">{t('profile.confirmator.edit')}</span>
                  </button>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className={`bg-[var(--color-primary-500)] text-white cursor-pointer px-6 py-2 rounded-lg font-semibold hover:bg-[var(--color-primary-400)] transition-colors duration-200 flex items-center gap-2 ${
                      isLoading ? 'opacity-70' : ''
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="max-xl:text-xs">{t('profile.confirmator.saving')}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm max-xl:text-xs">{t('profile.confirmator.save')}</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm max-xl:text-xs">{t('profile.confirmator.cancel')}</span>
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-between max-xl:px-3">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[var(--color-primary-50)] p-3 rounded-lg">
                  <UserCheck className="w-6 h-6 text-[var(--color-primary-500)]" />
                </div>
                <h2 className="text-xl font-bold text-gray-800">{t('profile.confirmator.title')}</h2>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-xl:px-3">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.confirmator.full_name')}</label>
                  {isEditing ? 
                    <>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.name && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.name[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.name || t('profile.confirmator.not_specified')}
                    </p>
                  }
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.confirmator.gender')}</label>
                  {isEditing ? 
                    <>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.gender ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      >
                        <option value="Male">{t('profile.confirmator.male')}</option>
                        <option value="Female">{t('profile.confirmator.female')}</option>
                      </select>
                      {fieldErrors.gender && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.gender[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.gender === "Male" ? t('profile.confirmator.male') : t('profile.confirmator.female') || t('profile.confirmator.not_specified')}
                    </p>
                  }
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.confirmator.rib')}</label>
                  {isEditing ? 
                    <>
                      <input
                        type="text"
                        name="rib"
                        value={formData.rib}
                        onChange={handleInputChange}
                        className={`w-full font-mono py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.rib ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.rib && (
                        <p className="text-red-500 text-xs mt-1 ">{fieldErrors.rib[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl font-mono">
                      {formData.rib || t('profile.confirmator.not_specified')}
                    </p>
                  }
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.confirmator.email')}</label>
                  {isEditing ? 
                    <>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.email && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.email[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.email || t('profile.confirmator.not_specified')}
                    </p>
                  }
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.confirmator.phone')}</label>
                  {isEditing ? 
                    <>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          fieldErrors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-[var(--color-primary-100)]'
                        }`}
                      />
                      {fieldErrors.phone && (
                        <p className="text-red-500 text-xs mt-1">{fieldErrors.phone[0]}</p>
                      )}
                    </>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.phone || t('profile.confirmator.not_specified')}
                    </p>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <ChangePassword userId={user.id} />
    </div>
  );
}